import {Injectable} from '@angular/core';
import {CanActivate, Router, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {ApiService} from './api.service';
import {AuthService} from './auth.service';
import {RoleService} from './role.service';

/**
 * Visitor Submit Entry flows.
 *
 * Requires:
 * 1. Existing core-ops role authorization (Admin/Member via canAccessCoreOps —
 *    Sales Manager and platform Super User are denied).
 * 2. Organization visitor_management_enabled === true, resolved from the same
 *    alert-direction-config source used by the sidebar (authenticated user's org).
 *
 * Fail closed when the feature flag is disabled, missing, or the request fails.
 * Backend visitor APIs remain the data-security boundary.
 */
@Injectable({
    providedIn: 'root',
})
export class VisitorSubmitAccessGuardService implements CanActivate {
    constructor(
        private auth: AuthService,
        private router: Router,
        private roleService: RoleService,
        private apiService: ApiService,
    ) {
    }

    canActivate(): Observable<boolean | UrlTree> | boolean | UrlTree {
        if (!this.auth.isAuthenticated()) {
            void this.router.navigate(['/login']);
            return false;
        }

        const userInfo = this.auth.getCurrentUserInfo();
        const groups = userInfo?.role;
        const denyUrl = this.router.createUrlTree([this.roleService.getDefaultRoute(groups)]);

        // Role gate first — do not fetch the feature flag for unauthorized roles.
        if (!this.roleService.canAccessCoreOps(groups)) {
            void this.router.navigate([this.roleService.getDefaultRoute(groups)]);
            return false;
        }

        // Authoritative org feature state (same endpoint as sidebar visitor visibility).
        return this.apiService.getAlertDirections().pipe(
            map((response: any) => {
                if (response?.visitor_management_enabled === true) {
                    return true;
                }
                return denyUrl;
            }),
            catchError(() => of(denyUrl)),
        );
    }
}
